import _ from "lodash";
import {TFunction} from "i18next";
import {RasterLayerType} from "../types/domain/rasterLayerType";
import {AggregatedLayerType} from "../types/domain/aggregatedLayerType";
import {MeasureSystemUtils} from "../types/domain/measureSystemUtils";
import {DateFormatType} from "../types/domain/dateFormat";
import {Dayjs} from "dayjs";
import {formatShortDate} from "./dateUtils";

export interface RasterLayerProps {
    type: RasterLayerType;
    name: string;
    units?: (msUtils: MeasureSystemUtils) => string;
    value: (value: number, msUtils: MeasureSystemUtils) => number;
    levels: {color: string; label?: string}[];
    isReversed: boolean;
}

export interface AggregatedLayerProps {
    type: AggregatedLayerType;
    name: string;
    units?: (msUtils: MeasureSystemUtils) => string;
    value: (value: number, msUtils: MeasureSystemUtils, dateStr?: string) => string;
    levels: {color: string; label?: string}[];
    isReversed: boolean;
    getColor(value: number, isNonIrrigatedType?: boolean): string;
    getLegendTick?: (value: number, dateFormat: DateFormatType, date?: Dayjs, t?: TFunction) => string;
}

export function isRasterLayerType(layerType: string) {
    return layerType === "raster";
}

export function isAggregatedLayerType(layerType: string) {
    return layerType === "aggregated";
}

export const AGGREGATED_LAYERS: (t?: TFunction) => AggregatedLayerProps[] = () => [
    {
        type: AggregatedLayerType.TIME_UNTIL_NEXT_IRRIGATION,
        name: "Date Next Irrigation",
        value: (value) => value.toString(),
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#b6e4b6",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
            {
                color: "#bdbdbd",
            },
        ],
        isReversed: false,
        getColor(this: AggregatedLayerProps, value: number, isNonIrrigatedType?: boolean): string {
            const {levels, isReversed} = this;

            const maxLevel = isNonIrrigatedType ? levels.length - 1 : levels.length - 2;
            let index = Math.max(0, Math.min(value, maxLevel));
            if (isReversed) {
                index = levels.length - 1 - index;
            }

            return levels[index].color;
        },
        getLegendTick: (value, dateFormat, date, t) => {
            if (!date) return "";
            if (value == 6) return `>${formatShortDate(date.add(value - 1, "days"), dateFormat)}`;
            if (value >= 7) return `${t ? t("map.map_layer_selection.legend.non_irrigated") : "non-irrigated"}`;

            return formatShortDate(date.add(value, "days"), dateFormat);
        },
    },
];
export const AGGREGATED_LAYERS_MAP: (t: TFunction) => Record<AggregatedLayerType, AggregatedLayerProps> = (
    t: TFunction,
) => _.keyBy(AGGREGATED_LAYERS(t), "type") as Record<AggregatedLayerType, AggregatedLayerProps>;

export const COMMON_RASTER_LAYERS: (t: TFunction) => RasterLayerProps[] = (t: TFunction) => [
    {
        type: RasterLayerType.MOISTURE_STATUS,
        name: "Moisture Status",
        value: (value) => value,
        levels: [
            {
                color: "#D91B14",
                label: t("map.map_layer_selection.legend.stressed"),
            },
            {
                color: "#ED7014",
                label: t("map.map_layer_selection.legend.limited"),
            },
            {
                color: "#49801B",
                label: t("map.map_layer_selection.legend.adequate"),
            },
            {
                color: "#1C07F8",
                label: t("map.map_layer_selection.legend.wet"),
            },
            {
                color: "#B29609",
                label: t("map.map_layer_selection.legend.sparse"),
            },
        ],
        isReversed: true,
    },
    {
        type: RasterLayerType.SOIL_MOISTURE_ROOT_ZONE,
        name: "Soil Moisture",
        value: (value) => value,
        units: (msUtils) => `${msUtils.getDistanceUnits()}\u00B3/${msUtils.getDistanceUnits()}\u00B3 or VMC(%)`,
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: true,
    },
    {
        type: RasterLayerType.DRY_MATTER_PRODUCTION,
        name: "Crop Growth",
        value: (value, msUtils) => msUtils.getProduction(value),
        units: (msUtils) => `${msUtils.getProductionUnits()}/${t("map.map_layer_selection.legend.day")}`,
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: true,
    },
    {
        type: RasterLayerType.DRY_MATTER_PRODUCTION_CUMULATIVE,
        name: "Total Crop Growth",
        value: (value, msUtils) => msUtils.getProduction(value),
        units: (msUtils) => msUtils.getProductionUnits(),
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: true,
    },
    {
        type: RasterLayerType.NDVI,
        name: "NDVI",
        value: (value) => value,
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: true,
    },
    {
        type: RasterLayerType.ACTUAL_EVAPOTRASPIRATION,
        name: "Actual ET",
        value: (value, msUtils) => msUtils.getRainfall(value),
        units: (msUtils) => `${msUtils.getRainfallUnits()}/${t("map.map_layer_selection.legend.day")}`,
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: true,
    },
    {
        type: RasterLayerType.LEAF_NITROGEN,
        name: "Leaf N",
        value: (value) => value,
        units: () => "%",
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: true,
    },
    {
        type: RasterLayerType.SOIL_WATER_POTENTIAL_ROOT_ZONE,
        name: "Soil Water Potential",
        value: (value) => {
            return Math.log10(Math.abs(value));
        },
        units: () => "pF",
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f46e43",
            },
            {
                color: "#fee08c",
            },
            {
                color: "#e5f498",
            },
            {
                color: "#64c0a6",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: true,
    },
    {
        type: RasterLayerType.SOIL_TEMPERATURE_DAILY_0CM,
        name: "Soil Temp Daily",
        value: (value, msUtils) => msUtils.getTemperature(value),
        units: (msUtils) => msUtils.getTemperatureUnits(),
        levels: [
            {
                color: "#5e4fa2",
            },
            {
                color: "#64c0a6",
            },
            {
                color: "#e5f498",
            },
            {
                color: "#fee08c",
            },
            {
                color: "#f46e43",
            },
            {
                color: "#9e0142",
            },
        ],
        isReversed: false,
    },
];

export const MADERA_RASTER_LAYERS: (t: TFunction) => RasterLayerProps[] = (t: TFunction) => [
    {
        type: RasterLayerType.ACTUAL_EVAPOTRASPIRATION_APPLIED_WATER_CUMULATIVE,
        name: "ETAW",
        value: (value, msUtils) => msUtils.getRainfall(value),
        units: (msUtils) => msUtils.getRainfallUnits(),
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: false,
    },
    ...COMMON_RASTER_LAYERS(t),
    {
        type: RasterLayerType.NONE,
        name: "None",
        value: () => 0,
        levels: [],
        isReversed: false,
    },
];

export const LEAF_TEMP_RASTER_LAYER: RasterLayerProps = {
    type: RasterLayerType.LEAF_TEMPERATURE,
    name: "Leaf Temperature (beta)",
    value: (value, msUtils) => msUtils.getTemperature(value),
    units: (msUtils) => msUtils.getTemperatureUnits(),
    levels: [
        {
            color: "#5e4fa2",
        },
        {
            color: "#64c0a6",
        },
        {
            color: "#e5f498",
        },
        {
            color: "#fee08c",
        },
        {
            color: "#f46e43",
        },
        {
            color: "#9e0142",
        },
    ],
    isReversed: false,
};

export const EMPTY_RASTER_LAYER = {
    type: RasterLayerType.NONE,
    name: "None",
    value: () => 0,
    levels: [],
    isReversed: false,
};
export const SUGARCANE_RASTER_LAYERS: RasterLayerProps[] = [
    {
        type: RasterLayerType.CANE_MOISTURE_CONTENT,
        name: "Cane Moisture Content",
        value: (value) => value,
        units: () => "%",
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: false,
    },
    {
        type: RasterLayerType.CANE_SUGER_CONTENT,
        name: "Cane Sugar Content",
        value: (value) => value,
        units: () => "%",
        levels: [
            {
                color: "#9e0142",
            },
            {
                color: "#f36c43",
            },
            {
                color: "#fedf8a",
            },
            {
                color: "#e6f599",
            },
            {
                color: "#67c2a5",
            },
            {
                color: "#5e4fa2",
            },
        ],
        isReversed: false,
    },
];

export const createLayersMapFromLayers: (layers: RasterLayerProps[]) => Record<RasterLayerType, RasterLayerProps> = (
    layers: RasterLayerProps[],
) => {
    return _.keyBy(layers, "type") as Record<RasterLayerType, RasterLayerProps>;
};
